import React, { useState, useRef } from 'react'
import {
	List,
	ListItem,
	ListItemAvatar,
	IconButton,
	Box,
	Typography,
	Button,
	FormControlLabel,
	Checkbox,
	TextField,
} from '@mui/material'
import { AiOutlineEdit } from 'react-icons/ai'
import { IoIosArrowRoundUp, IoIosArrowRoundDown } from 'react-icons/io'
import { observer } from 'mobx-react-lite'
import statusStore from '../../../stores/statusStore'
import logger from '../../../utils/logger'

const StatusItem = observer(({ status }) => {
	const mayManage = statusStore.canManageDictionaries
	const [editing, setEditing] = useState(false)
	const [editedTitleRealtor, setEditedTitleRealtor] = useState(
		status.titleRealtor
	)
	const [editedTitleClient, setEditedTitleClient] = useState(status.titleClient)
	const [editedColor, setEditedColor] = useState(status.color)
	const [editedIsVisibleToClient, setEditedIsVisibleToClient] = useState(
		status.isVisibleToClient
	)
	const [clientTitleError, setClientTitleError] = useState('')

	const colorInputRef = useRef(null)

	const handleEdit = () => {
		logger.log('Редактирование статуса id:', status.id)
		setEditing(true)
	}

	const handleCancel = () => {
		logger.log('Отмена редактирования статуса id:', status.id)
		// Сбрасываем значения к исходным
		setEditedTitleRealtor(status.titleRealtor)
		setEditedTitleClient(status.titleClient)
		setEditedColor(status.color)
		setEditedIsVisibleToClient(status.isVisibleToClient)
		setEditing(false)
	}

	const handleSave = async () => {
		// Если статус должен показываться клиенту, проверяем, что название для клиента не пустое
		if (editedIsVisibleToClient && editedTitleClient.trim() === '') {
			setClientTitleError(
				'"Название статуса для клиента" не должно быть пустым'
			)
			return
		} else {
			setClientTitleError('')
		}

		const updatedStatus = {
			id: status.id,
			titleRealtor: editedTitleRealtor,
			titleClient: editedTitleClient,
			color: editedColor,
			isVisibleToClient: editedIsVisibleToClient,
			sortOrder: status.sortOrder, // сортировка пока не меняется
			// можно передать и другие поля, если они нужны
		}

		logger.log('Сохранить статус id:', status.id, updatedStatus)
		await statusStore.updateStatus(updatedStatus)
		setEditing(false)
	}

	const handleDelete = () => {
		if (window.confirm('Вы действительно хотите удалить этот статус?')) {
			logger.log('Удалить статус id:', status.id)
			statusStore.deleteStatus(status.id)
		}
	}

	const handleMoveUp = async () => {
		logger.log('Переместить статус вверх id:', status.id)
		await statusStore.moveStatus(status.id, 'up')
	}

	const handleMoveDown = async () => {
		logger.log('Переместить статус вниз id:', status.id)
		await statusStore.moveStatus(status.id, 'down')
	}

	const handleColorChange = e => {
		setEditedColor(e.target.value)
	}

	if (editing) {
		return (
			<ListItem
				key={status.id}
				divider
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-start',
					paddingY: 1,
				}}
			>
				{/* Строка для выбора цвета с кнопкой "Изменить цвет" */}
				<Box
					sx={{
						width: '100%',
						display: 'flex',
						alignItems: 'center',
						marginBottom: 1,
					}}
				>
					<Box
						sx={{
							width: 40,
							height: 40,
							borderRadius: '50%',
							backgroundColor: editedColor,
							marginRight: 1,
						}}
					/>
					<Button
						variant='outlined'
						onClick={() => colorInputRef.current.click()}
					>
						Изменить цвет
					</Button>
					<input
						type='color'
						ref={colorInputRef}
						value={editedColor}
						onChange={handleColorChange}
						style={{ display: 'none' }}
					/>
				</Box>

				{/* Поле для редактирования названия для риелтора */}
				<Box sx={{ width: '100%', marginBottom: 1 }}>
					<Typography variant='body2'>
						Название статуса внутри системы:
					</Typography>
					<TextField
						type='text'
						value={editedTitleRealtor}
						onChange={e => setEditedTitleRealtor(e.target.value)}
						style={{ width: '100%', padding: '4px' }}
						size='small'
						sx={{ maxWidth: '500px' }}
					/>
				</Box>

				{/* Чекбокс для управления видимостью статуса для клиента */}
				<Box sx={{ width: '100%', marginBottom: 1 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={editedIsVisibleToClient}
								onChange={e => setEditedIsVisibleToClient(e.target.checked)}
							/>
						}
						label='Показывать клиенту?'
					/>
				</Box>

				{/* Если статус виден клиенту – поле для редактирования названия для клиента */}
				{editedIsVisibleToClient && (
					<Box sx={{ width: '100%', marginBottom: 1 }}>
						<Typography variant='body2'>
							Название статуса для клиента:
						</Typography>
						<TextField
							type='text'
							value={editedTitleClient}
							onChange={e => setEditedTitleClient(e.target.value)}
							style={{ width: '100%', padding: '4px' }}
							size='small'
							sx={{ maxWidth: '500px' }}
							error={Boolean(clientTitleError)}
						/>
						{clientTitleError && (
							<Typography variant='caption' sx={{ color: 'red' }}>
								<div>{clientTitleError}</div>
							</Typography>
						)}
					</Box>
				)}

				{/* Кнопки для изменения порядка сортировки и удаления */}
				<Box
					sx={{
						width: '100%',
						display: 'flex',
						justifyContent: 'space-between',
						marginBottom: 1,
					}}
				>
					<Box>
						<IconButton onClick={handleMoveUp}>
							<IoIosArrowRoundUp size={20} />
						</IconButton>
						<IconButton onClick={handleMoveDown}>
							<IoIosArrowRoundDown size={20} />
						</IconButton>
					</Box>
				</Box>

				{/* Кнопки Отмена и Сохранить */}

				<Box
					sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}
				>
					<Button
						variant='outlined'
						onClick={handleDelete}
						size='small'
						color='error'
						sx={{ marginRight: 1 }}
					>
						Удалить
					</Button>
					<Button
						variant='outlined'
						onClick={handleCancel}
						size='small'
						sx={{ marginRight: 1 }}
					>
						Отмена
					</Button>
					<Button variant='contained' onClick={handleSave} size='small'>
						Сохранить
					</Button>
				</Box>
			</ListItem>
		)
	}

	return (
		<ListItem
			key={status.id}
			divider
			sx={{ display: 'flex', alignItems: 'center', paddingY: 1 }}
		>
			<ListItemAvatar>
				<Box
					sx={{
						width: 40,
						height: 40,
						borderRadius: '50%',
						backgroundColor: status.color,
					}}
				/>
			</ListItemAvatar>
			<Box sx={{ flex: 1, minWidth: 0, paddingLeft: 1 }}>
				<Typography variant='body1' noWrap>
					{status.titleRealtor}
				</Typography>
				{status.isVisibleToClient && (
					<Typography variant='body2' color='text.secondary' noWrap>
						Название для клиента: {status.titleClient}
					</Typography>
				)}
			</Box>
			{mayManage && <IconButton edge='end' onClick={handleEdit}>
				<AiOutlineEdit size={20} />
			</IconButton>}
		</ListItem>
	)
})

const StatusesList = observer(({ statuses }) => {
	return (
		<List>
			{statuses.map(status => (
				<StatusItem key={status.id} status={status} />
			))}
		</List>
	)
})

export default StatusesList
